/**
 * Haley Homepage — 2026 Interactive Features
 * - Scroll-triggered animations (IntersectionObserver)
 * - FAQ accordions
 * - Smooth scroll navigation
 * - Sticky CTA bar
 * - Counter animations
 */

(function () {
  'use strict';

  const root = document.querySelector('.haley-homepage');
  if (!root) return;

  /* ==========================================================
     1. SCROLL-TRIGGERED ANIMATIONS
     ========================================================== */
  const animElements = root.querySelectorAll('[data-reveal]');

  if (animElements.length && 'IntersectionObserver' in window) {
    const animObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            animObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    animElements.forEach((el) => animObserver.observe(el));
  } else {
    // Fallback: show everything
    animElements.forEach((el) => el.classList.add('is-visible'));
  }

  /* ==========================================================
     1.5 SCROLL INDICATOR AUTO-HIDE (Mobile)
     ========================================================== */
  const scrollIndicator = root.querySelector('.hp-hero__scroll-indicator');
  if (scrollIndicator) {
    let hasScrolled = false;

    const hideScrollIndicator = () => {
      if (!hasScrolled && window.scrollY > 50) {
        hasScrolled = true;
        scrollIndicator.style.opacity = '0';
        scrollIndicator.style.transition = 'opacity 0.3s ease-out';
        window.removeEventListener('scroll', hideScrollIndicator);
      }
    };

    window.addEventListener('scroll', hideScrollIndicator, { passive: true });
  }

  /* ==========================================================
     2. FAQ ACCORDIONS
     ========================================================== */
  const faqItems = root.querySelectorAll('.hp-faq-item');

  faqItems.forEach((item) => {
    const question = item.querySelector('.hp-faq-question');
    const answer = item.querySelector('.hp-faq-answer');

    if (question && answer) {
      question.addEventListener('click', () => {
        const isOpen = item.classList.contains('active');

        // Close all others
        faqItems.forEach((other) => {
          if (other !== item) {
            other.classList.remove('active');
            const otherAnswer = other.querySelector('.hp-faq-answer');
            if (otherAnswer) otherAnswer.style.maxHeight = null;
          }
        });

        // Toggle current
        item.classList.toggle('active', !isOpen);
        if (!isOpen) {
          answer.style.maxHeight = answer.scrollHeight + 'px';
        } else {
          answer.style.maxHeight = null;
        }
      });
    }
  });

  /* ==========================================================
     4. SMOOTH SCROLL FOR ANCHOR LINKS
     ========================================================== */
  root.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener('click', (e) => {
      const targetId = anchor.getAttribute('href');
      if (targetId === '#') return;

      const target = document.querySelector(targetId);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    });
  });

  /* ==========================================================
     5. STICKY BOTTOM CTA BAR
     ========================================================== */
  const stickyBar = root.querySelector('.hp-sticky-bottom');
  const hero = root.querySelector('.hp-hero');

  if (stickyBar && hero && 'IntersectionObserver' in window) {
    const heroObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
        });
      },
      { threshold: 0 }
    );

    heroObserver.observe(hero);

    // Hide when near footer
    const footer = document.querySelector('footer');
    if (footer) {
      const footerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              stickyBar.classList.remove('is-visible');
            }
          });
        },
        { threshold: 0.1 }
      );

      footerObserver.observe(footer);
    }
  }

  /* ==========================================================
     6. COUNTER ANIMATIONS
     ========================================================== */
  const counters = root.querySelectorAll('[data-counter]');

  if (counters.length && 'IntersectionObserver' in window) {
    const counterObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCounter(entry.target);
            counterObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    counters.forEach((el) => counterObserver.observe(el));
  }

  function animateCounter(el) {
    const rawValue = el.dataset.counter;
    const target = parseFloat(rawValue);
    if (isNaN(target)) return;

    const unit = rawValue.replace(/[0-9.]/g, '');
    const isFloat = rawValue.includes('.');
    const duration = 1500;
    const startTime = performance.now();

    function update(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = target * eased;

      const displayVal = isFloat ? current.toFixed(1) : Math.round(current);
      el.textContent = displayVal + unit;

      if (progress < 1) {
        requestAnimationFrame(update);
      } else {
        const finalNum = isFloat ? target.toFixed(1) : target;
        el.textContent = finalNum + unit;
      }
    }

    requestAnimationFrame(update);
  }

  /* ==========================================================
     7. TECH NARRATIVE SCROLL PROGRESS
     ========================================================== */
  const techSection = root.querySelector('.hp-tech-narrative');
  const techStages = root.querySelectorAll('.hp-tech-stage');

  if (techSection && techStages.length && 'IntersectionObserver' in window) {
    const stageObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.3, rootMargin: '0px 0px -100px 0px' }
    );

    techStages.forEach((stage) => stageObserver.observe(stage));
  }

  function prepareVideo(video, preload = 'metadata') {
    if (!video || video.src || !video.dataset.src) return;
    video.preload = preload;
    video.src = video.dataset.src;
    video.load();
  }

  /* ==========================================================
     8. VIDEO REELS (CLICK TO PLAY) - Legacy
     ========================================================== */
  const reelCards = root.querySelectorAll('.hp-reel-card');

  if (reelCards.length) {
    let currentlyPlaying = null;

    reelCards.forEach((card) => {
      const video = card.querySelector('video');
      const playBtn = card.querySelector('.hp-reel-card__play');

      if (video && playBtn) {
        // Click to play/pause
        card.addEventListener('click', () => {
          if (video.paused) {
            prepareVideo(video, 'auto');

            // Pause any currently playing video
            if (currentlyPlaying && currentlyPlaying !== video) {
              currentlyPlaying.pause();
              currentlyPlaying.closest('.hp-reel-card').classList.remove('is-playing');
            }

            const playPromise = video.play();
            if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
            card.classList.add('is-playing');
            currentlyPlaying = video;
          } else {
            video.pause();
            card.classList.remove('is-playing');
            currentlyPlaying = null;
          }
        });

        // Pause when video ends (even though loop is on, for safety)
        video.addEventListener('ended', () => {
          card.classList.remove('is-playing');
          currentlyPlaying = null;
        });
      }
    });

    // Pause videos when scrolling out of view
    if ('IntersectionObserver' in window) {
      const videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target.querySelector('video');
            if (!video) return;

            if (entry.isIntersecting) {
              prepareVideo(video);
            } else {
              if (video && !video.paused) {
                video.pause();
                entry.target.classList.remove('is-playing');
                if (currentlyPlaying === video) {
                  currentlyPlaying = null;
                }
              }
            }
          });
        },
        { threshold: 0.05, rootMargin: '500px 0px' }
      );

      reelCards.forEach((card) => videoObserver.observe(card));
    }
  }

  /* ==========================================================
     8b. REVIEWS CARD STACK (紙牌堆 · 任意方向掟走)
     A hand-stacked pile of real reviews. Throw the top card up /
     left / right (drag, arrow keys, or the button) to surface the
     next one. Only the active card loads + plays its video.
     ========================================================== */
  const reelStack = root.querySelector('[data-reelstack]');

  if (reelStack) {
    const deck = reelStack.querySelector('.hp-reelstack__deck');
    const cards = deck
      ? Array.prototype.slice.call(deck.querySelectorAll('[data-reel-card]'))
      : [];
    const total = cards.length;

    if (deck && total) {
      const countEl = reelStack.querySelector('[data-reel-count]');
      const leftEl = reelStack.querySelector('[data-reel-left]');
      const nextBtn = reelStack.querySelector('[data-reel-next]');
      const peek = Math.min(3, total - 1);
      // Rotations that make the pile look hand-stacked.
      const rot = [
        'rotate(-4deg) translate(-9px, 10px)',
        'rotate(3.4deg) translate(11px, 19px)',
        'rotate(-2.4deg) translate(-4px, 27px)',
      ];
      const reduceMotion =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      let idx = 0;
      let muted = true;
      let inView = false;
      let busy = false;

      const cardAt = (depth) => cards[(idx + depth) % total];

      function paintVideos() {
        cards.forEach((card) => {
          const video = card.querySelector('video');
          if (!video) return;
          if (card.classList.contains('is-active') && inView) {
            video.muted = muted;
            prepareVideo(video, 'auto');
            const playPromise = video.play();
            if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
          } else if (!video.paused) {
            video.pause();
          }
        });
      }

      function layout() {
        for (let depth = 0; depth < total; depth++) {
          const card = cardAt(depth);
          if (depth === 0) {
            card.classList.add('is-active');
            card.style.zIndex = '30';
            card.style.opacity = '';
            card.style.transform = '';
            card.style.pointerEvents = 'auto';
            card.setAttribute('aria-hidden', 'false');
          } else {
            card.classList.remove('is-active');
            const clamped = Math.min(depth, peek);
            card.style.zIndex = String(30 - depth);
            card.style.transform = rot[clamped - 1] + ' scale(' + (1 - clamped * 0.03) + ')';
            card.style.opacity = depth > peek ? '0' : depth === peek ? '0.65' : '1';
            card.style.pointerEvents = 'none';
            card.setAttribute('aria-hidden', 'true');
          }
        }
        if (countEl) countEl.textContent = idx + 1 + ' / ' + total;
        if (leftEl) leftEl.textContent = '仲有 ' + (total - 1 - idx) + ' 條';
        paintVideos();
      }

      function commit() {
        idx = (idx + 1) % total;
        busy = false;
        layout();
      }

      // Fling the active card out of the pile, then surface the next one.
      function throwCard(dir) {
        if (busy) return;
        busy = true;
        const card = cardAt(0);

        const settle = () => {
          card.style.transition = 'none';
          card.style.transform = '';
          card.style.opacity = '';
          commit();
          requestAnimationFrame(() => {
            card.style.transition = '';
          });
        };

        if (reduceMotion) {
          card.style.transition = 'opacity .2s ease';
          card.style.opacity = '0';
          window.setTimeout(settle, 200);
          return;
        }

        let tf;
        if (dir === 'left') tf = 'translate(-480px, 60px) rotate(-12deg)';
        else if (dir === 'right') tf = 'translate(480px, 60px) rotate(12deg)';
        else tf = 'translate(0, -720px) rotate(-4deg)';

        card.style.transition = 'transform .26s ease-in, opacity .26s ease-in';
        card.style.transform = tf;
        card.style.opacity = '0';
        window.setTimeout(settle, 250);
      }

      // Pointer drag → throw in any direction (up / left / right).
      deck.addEventListener('pointerdown', (event) => {
        if (busy) return;
        const card = cardAt(0);
        if (!card.contains(event.target)) return;
        if (event.target.closest('.hp-reelstack__mute')) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        const startX = event.clientX;
        const startY = event.clientY;
        let dx = 0;
        let dy = 0;
        let live = true;
        card.style.transition = 'none';
        card.classList.add('is-grabbing');

        const move = (ev) => {
          if (!live) return;
          dx = ev.clientX - startX;
          dy = ev.clientY - startY;
          card.style.transform =
            'translate(' + dx + 'px, ' + Math.min(dy, 24) + 'px) rotate(' + dx * 0.05 + 'deg)';
        };

        const up = () => {
          live = false;
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', up);
          card.classList.remove('is-grabbing');

          const horizontal = Math.abs(dx) > 90 && Math.abs(dx) >= -dy;
          const passed = dy < -70 || Math.abs(dx) > 90;

          if (passed) {
            throwCard(horizontal ? (dx > 0 ? 'right' : 'left') : 'up');
          } else {
            card.style.transition = 'transform .3s cubic-bezier(.22, .8, .36, 1)';
            card.style.transform = '';
            window.setTimeout(() => {
              card.style.transition = '';
            }, 320);
          }
        };

        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
      });

      // Keyboard: arrows throw directionally, Enter / Space advance.
      // Skip when the mute button owns focus so it isn't a double action.
      deck.addEventListener('keydown', (event) => {
        if (event.target.closest('.hp-reelstack__mute')) return;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          throwCard('left');
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          throwCard('right');
        } else if (event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          throwCard('up');
        }
      });

      if (nextBtn) {
        nextBtn.addEventListener('click', () => throwCard('up'));
      }

      // Mute toggle (delegated so it survives re-layout).
      reelStack.addEventListener('click', (event) => {
        if (!event.target.closest('.hp-reelstack__mute')) return;
        muted = !muted;
        reelStack.classList.toggle('is-muted', muted);
        const video = cardAt(0).querySelector('video');
        if (video) video.muted = muted;
      });

      // Progress bar tracks real playback on the active video.
      cards.forEach((card) => {
        const video = card.querySelector('video');
        const bar = card.querySelector('.hp-reelstack__progress > span');
        if (!video || !bar) return;
        video.addEventListener('timeupdate', () => {
          if (video.duration) bar.style.width = (video.currentTime / video.duration) * 100 + '%';
        });
      });

      // Only play while the section is on screen.
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              inView = entry.isIntersecting;
              if (inView) {
                paintVideos();
              } else {
                cards.forEach((card) => {
                  const video = card.querySelector('video');
                  if (video && !video.paused) video.pause();
                });
              }
            });
          },
          { threshold: 0.2 }
        );
        io.observe(reelStack);
      } else {
        inView = true;
      }

      reelStack.classList.toggle('is-muted', muted);
      layout();
    }
  }

  /* ==========================================================
     9. WHATSAPP FLOATING BUTTON TOOLTIP
     ========================================================== */
  const whatsappPill = root.querySelector('.hp-whatsapp-pill');

  if (whatsappPill) {
    // Show tooltip after 3 seconds on page
    setTimeout(() => {
      whatsappPill.classList.add('show-tooltip');

      // Hide tooltip after 5 seconds
      setTimeout(() => {
        whatsappPill.classList.remove('show-tooltip');
      }, 5000);
    }, 3000);
  }

  /* ==========================================================
     10. LAZY LOAD IMAGES
     ========================================================== */
  const lazyImages = root.querySelectorAll('img[data-src]');

  if (lazyImages.length && 'IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            imageObserver.unobserve(img);
          }
        });
      },
      { rootMargin: '50px 0px' }
    );

    lazyImages.forEach((img) => imageObserver.observe(img));
  } else {
    // Fallback: load all images
    lazyImages.forEach((img) => {
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    });
  }

})();
